import { css } from "@emotion/react";
import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router";
import {
  Button,
  Flex,
  Heading,
  Icon,
  Icons,
  Loading,
  Popover,
  Text,
  Menu,
  MenuItem,
  MenuTrigger,
} from "@phoenix/components";
import { useNotifyError, useNotifySuccess } from "@phoenix/contexts";
import { authFetch } from "../../authFetch";
import { prependBasename } from "../../utils/routingUtils";

interface MonitorStatus {
  template_id: string;
  name: string;
  category: string;
  status: "running" | "stopped";
  project_name: string | null;
  projects?: string[];
  started_at: string | null;
  last_check_at: string | null;
  processed_traces: number;
  annotations_logged: number;
  new_traces: number;
  poll_interval: number;
  stopped_at: string | null;
}

const pageContainer = css`
  display: flex;
  flex-direction: column;
  gap: var(--global-dimension-size-150);
  padding: var(--global-dimension-size-150);
  width: 100%;
  height: calc(100vh - 64px);
  overflow-y: auto;
  box-sizing: border-box;
  background-color: var(--global-background-color-default);
`;

const headerRow = css`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--global-dimension-size-100);
  flex-wrap: wrap;
`;

const lastUpdatedContainer = css`
  display: flex;
  align-items: center;
  gap: var(--global-dimension-size-50);
  color: var(--global-text-color-500);
  font-size: 0.875rem;
  background: var(--global-color-gray-100);
  padding: 0.5rem 1rem;
  border-radius: 999px;
  border: 1px solid var(--global-border-color-default);
  cursor: pointer;
  transition: background 150ms ease;

  &:hover {
    background: var(--global-color-gray-200);
    color: var(--global-text-color-900);
  }
`;

const cardsGrid = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: var(--global-dimension-size-100);
  width: 100%;
`;

const summaryCard = (colorTheme: "green" | "grey" | "blue" | "orange") => {
  let badgeBg = "rgba(107, 114, 128, 0.1)";
  let badgeColor = "var(--global-text-color-700)";
  let borderHoverColor = "rgba(107, 114, 128, 0.2)";

  if (colorTheme === "green") {
    badgeBg = "rgba(16, 185, 129, 0.12)";
    badgeColor = "#10B981";
    borderHoverColor = "rgba(16, 185, 129, 0.3)";
  } else if (colorTheme === "blue") {
    badgeBg = "rgba(59, 130, 246, 0.12)";
    badgeColor = "#3B82F6";
    borderHoverColor = "rgba(59, 130, 246, 0.3)";
  } else if (colorTheme === "orange") {
    badgeBg = "rgba(245, 158, 11, 0.12)";
    badgeColor = "#F59E0B";
    borderHoverColor = "rgba(245, 158, 11, 0.3)";
  }

  return css`
    display: flex;
    align-items: center;
    gap: var(--global-dimension-size-100);
    border-radius: 20px;
    border: 1px solid var(--global-border-color-default);
    background: var(--global-color-gray-75);
    padding: var(--global-dimension-size-100);
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.03);
    transition: transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease;

    &:hover {
      transform: translateY(-2px);
      border-color: ${borderHoverColor};
      box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
    }

    .icon-badge {
      width: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 14px;
      background: ${badgeBg};
      color: ${badgeColor};
      font-size: 1.5rem;
      flex-shrink: 0;
    }

    .card-content {
      display: flex;
      flex-direction: column;
      gap: 2px;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .card-value {
      font-size: 1.4rem;
      font-weight: 900;
      color: #0f172a;
    }

    .card-title {
      font-size: 0.95rem;
      font-weight: 800;
      color: #0f172a;
    }

    .card-subtitle {
      font-size: 0.825rem;
      font-weight: 700;
      color: #0f172a;
    }
  `;
};

const tablesLayoutGrid = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: var(--global-dimension-size-150);
  width: 100%;
`;

const tableSectionCard = (colorTheme: "green" | "grey") => css`
  display: flex;
  flex-direction: column;
  border-radius: 28px;
  border: 1px solid var(--global-border-color-default);
  background: var(--global-color-gray-75);
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.05);
  padding: var(--global-dimension-size-125);
  box-sizing: border-box;
  min-height: 480px;
`;

const tableHeader = (colorTheme: "green" | "grey") => {
  const dotColor = colorTheme === "green" ? "#10B981" : "#9CA3AF";
  return css`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: var(--global-dimension-size-100);

    .title-row {
      display: flex;
      align-items: center;
      gap: var(--global-dimension-size-50);

      h3,
      [data-level="3"] {
        font-weight: 700 !important;
        color: #0f172a !important;
        font-size: 1.2rem !important;
      }
    }

    .status-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: ${dotColor};
      flex-shrink: 0;
      box-shadow: 0 0 8px ${dotColor};
    }

    .subtitle {
      font-size: 0.8rem;
      font-weight: 500;
      color: #64748b;
    }
  `;
};

const tableContainer = css`
  width: 100%;
  overflow-x: auto;
  flex: 1;
`;

const customTable = css`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 13px;

  th {
    color: #0f172a !important;
    font-weight: 800 !important;
    font-size: 13px !important;
    padding: 0.75rem 1rem;
    border-bottom: 2px solid var(--global-border-color-default, #cbd5e1);
    white-space: nowrap;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    text-shadow: 0 0 0.25px currentColor;
  }

  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--global-border-color-default);
    color: #1e293b;
    font-size: 13px !important;
    font-weight: 500;
    vertical-align: middle;
  }

  tr:hover td {
    background: rgba(15, 23, 42, 0.02);
  }

  .eval-cell {
    display: flex;
    align-items: center;
    gap: var(--global-dimension-size-50);
    font-weight: 500;
    font-size: 13px !important;
  }

  .status-indicator-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .status-indicator-icon.running {
    background: rgba(16, 185, 129, 0.12);
    color: #10B981;
  }

  .status-indicator-icon.stopped {
    background: rgba(107, 114, 128, 0.1);
    color: var(--global-text-color-500);
  }

  .never-started {
    color: #F97316;
    font-weight: 500;
    font-size: 13px !important;
  }
`;

const footerLinkRow = css`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: auto;
  padding-top: var(--global-dimension-size-100);
`;

const footerLink = css`
  display: inline-flex;
  align-items: center;
  gap: var(--global-dimension-size-25);
  color: var(--global-text-color-900);
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  cursor: pointer;
  transition: gap 150ms ease;

  &:hover {
    color: var(--global-color-gray-900);
    gap: var(--global-dimension-size-50);
    text-decoration: underline;
  }
`;

const bottomAlertBanner = css`
  display: flex;
  align-items: center;
  gap: var(--global-dimension-size-50);
  background: rgba(59, 130, 246, 0.05);
  border: 1px solid rgba(59, 130, 246, 0.15);
  color: #1d4ed8;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  font-size: 0.875rem;
  margin-top: var(--global-dimension-size-50);

  .info-icon {
    display: flex;
    align-items: center;
    color: #3b82f6;
  }
`;

const formatCategory = (category: string): string => {
  if (category === "sql_evaluation") return "SQL Evaluation";
  if (category === "rag_evaluation") return "RAG Evaluation";
  if (category === "tool_call") return "Tool Call";
  if (category === "behavior_evaluation") return "Behavior Evaluation";
  if (category === "multilingual_evaluation") return "Multilingual Evaluation";
  if (category === "output_format") return "Output Format";
  if (category === "code_generation") return "Code Generation";
  if (category === "model_graded_metrics") return "Safety Evaluation";
  if (category === "dataset_evaluation") return "Dataset Evaluation";
  return category
    .replace(/_/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
};

const normalizeIso = (isoString?: string | null): string | null => {
  if (!isoString) return null;
  let normalized = isoString;
  if (!isoString.endsWith("Z") && !/[+-]\d{2}:\d{2}$/.test(isoString)) {
    normalized = isoString + "Z";
  }
  return normalized;
};

const formatDateTime = (isoString?: string | null): string => {
  if (!isoString) return "Never Started";
  try {
    const normalized = normalizeIso(isoString);
    if (!normalized) return "Never Started";
    const date = new Date(normalized);
    if (isNaN(date.getTime())) return "Never Started";
    const day = String(date.getDate()).padStart(2, "0");
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const strHours = String(hours).padStart(2, "0");
    return `${day}-${month}-${year} ${strHours}:${minutes} ${ampm}`;
  } catch {
    return "Never Started";
  }
};

const datasetTemplateIds = ["ground-truth", "llm-assisted", "reasoning", "regression-testing", "output-drift"];

const laterDate = (a: string | null, b: string | null): string | null => {
  if (!a) return b;
  if (!b) return a;
  const normA = normalizeIso(a);
  const normB = normalizeIso(b);
  if (!normA) return b;
  if (!normB) return a;
  return new Date(normB).getTime() > new Date(normA).getTime() ? b : a;
};

/**
 * The status endpoint returns one row per (template, project) target, so a
 * template running against several projects would otherwise fan out into many
 * rows. Collapse to one row per template (= evaluation + category), listing
 * every project comma-separated; running wins over stopped for the group.
 */
const groupStatuses = (items: MonitorStatus[]): MonitorStatus[] => {
  const byTemplate = new Map<string, MonitorStatus>();
  const order: string[] = [];

  for (const item of items) {
    const project = item.project_name;
    const existing = byTemplate.get(item.template_id);
    if (!existing) {
      byTemplate.set(item.template_id, {
        ...item,
        projects: project ? [project] : [],
      });
      order.push(item.template_id);
      continue;
    }

    if (project && !existing.projects!.includes(project)) {
      existing.projects!.push(project);
    }
    if (item.status === "running") {
      if (existing.status !== "running") {
        existing.status = "running";
        existing.started_at = item.started_at;
        existing.stopped_at = null;
      } else {
        existing.started_at = laterDate(existing.started_at, item.started_at);
      }
    } else if (existing.status === "stopped") {
      existing.started_at = laterDate(existing.started_at, item.started_at);
      existing.stopped_at = laterDate(existing.stopped_at, item.stopped_at);
    }
    existing.last_check_at = laterDate(existing.last_check_at, item.last_check_at);
    existing.processed_traces += item.processed_traces;
    existing.annotations_logged += item.annotations_logged;
    existing.new_traces += item.new_traces;
  }

  return order.map((templateId) => {
    const grouped = byTemplate.get(templateId)!;
    return {
      ...grouped,
      project_name: grouped.projects!.length > 0 ? grouped.projects!.join(", ") : null,
    };
  });
};

export function EvaluationActivityPage() {
  const notifySuccess = useNotifySuccess();
  const notifyError = useNotifyError();
  const navigate = useNavigate();

  const [statuses, setStatuses] = useState<MonitorStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<string>("");
  const [showAllRunning, setShowAllRunning] = useState(false);
  const [showAllNotRunning, setShowAllNotRunning] = useState(false);

  const fetchStatuses = useCallback(
    async (showSilent = false) => {
      if (!showSilent) setLoading(true);
      try {
        // Fetch trace monitors list
        const res = await authFetch(prependBasename("/v1/templates/status"));
        if (!res.ok) {
          throw new Error(`Failed to load monitor statuses: ${res.statusText}`);
        }
        const monitors = (await res.json()) as MonitorStatus[];

        // Fetch dataset evaluation statuses in parallel
        const datasetPromises = datasetTemplateIds.map((id) =>
          authFetch(prependBasename(`/v1/templates/dataset-status/${id}`))
        );
        const datasetResponses = await Promise.all(datasetPromises);

        const datasetStatuses: MonitorStatus[] = [];
        for (let i = 0; i < datasetResponses.length; i++) {
          const dRes = datasetResponses[i];
          const dId = datasetTemplateIds[i];
          if (dRes.ok) {
            const data = await dRes.json();
            let friendlyName = "Reasoning Evaluation";
            if (dId === "ground-truth") friendlyName = "Ground-Truth Evaluation";
            else if (dId === "llm-assisted") friendlyName = "Automated Model Evaluation";
            else if (dId === "regression-testing") friendlyName = "Regression Testing";
            else if (dId === "output-drift") friendlyName = "Output Drift Detection";

            datasetStatuses.push({
              template_id: dId,
              name: friendlyName,
              category: "dataset_evaluation",
              status: data.status === "running" ? "running" : "stopped",
              project_name: data.project_name || null,
              started_at: data.started_at || null,
              last_check_at: null,
              processed_traces: 0,
              annotations_logged: 0,
              new_traces: 0,
              poll_interval: 0,
              stopped_at: data.stopped_at || null,
            });
          }
        }

        // Combine both lists
        const combined = [...monitors, ...datasetStatuses];
        setStatuses(combined);
        setLastUpdated(new Date().toISOString());
      } catch (err) {
        notifyError({
          title: "Failed to load evaluation activities",
          message: err instanceof Error ? err.message : "Unknown error",
        });
      } finally {
        if (!showSilent) setLoading(false);
      }
    },
    [notifyError]
  );

  useEffect(() => {
    fetchStatuses();

    // Refresh every 5 seconds dynamically
    const interval = setInterval(() => {
      fetchStatuses(true);
    }, 5000);

    return () => clearInterval(interval);
  }, [fetchStatuses]);

  const handleStopMonitor = async (
    templateId: string,
    friendlyName: string,
    isDataset: boolean,
    projectName: string | null
  ) => {
    try {
      const endpoint = isDataset ? "/v1/templates/stop-dataset" : "/v1/templates/stop";
      const res = await authFetch(prependBasename(endpoint), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          template_id: templateId,
          ...(isDataset ? {} : { project_name: projectName }),
        }),
      });
      if (!res.ok) {
        throw new Error("Failed to stop evaluation");
      }
      notifySuccess({
        title: "Evaluation stopped",
        message: `${friendlyName} has been stopped successfully.`,
      });
      fetchStatuses(true);
    } catch (err) {
      notifyError({
        title: "Failed to stop evaluation",
        message: err instanceof Error ? err.message : "Unknown error",
      });
    }
  };

  const handleStartMonitor = (category: string, templateId: string) => {
    if (category === "dataset_evaluation") {
      navigate(`/templates/dataset/${templateId}`);
    } else {
      navigate(`/templates/${category}`);
    }
  };

  const groupedStatuses = groupStatuses(statuses);
  const runningEvaluations = groupedStatuses.filter((s) => s.status === "running");
  const notRunningEvaluations = groupedStatuses.filter((s) => s.status === "stopped");

  // Determine latest start date
  const latestStartedAt = groupedStatuses
    .filter((s) => s.started_at)
    .reduce((latest, current) => {
      if (!latest || !current.started_at) return current.started_at;
      return new Date(current.started_at) > new Date(latest) ? current.started_at : latest;
    }, null as string | null);

  const displayedRunning = showAllRunning ? runningEvaluations : runningEvaluations.slice(0, 8);
  const displayedNotRunning = showAllNotRunning ? notRunningEvaluations : notRunningEvaluations.slice(0, 8);

  return (
    <div css={pageContainer}>
      {/* Header section */}
      <div css={headerRow}>
        <div>
          <Heading
            level={1}
            css={css`
              font-size: 21px !important;
              font-weight: 700 !important;
            `}
          >
            Evaluation Activity
          </Heading>
          <Text
            color="text-500"
            css={css`
              font-size: 13px !important;
              margin-top: var(--global-dimension-size-25);
              display: block;
            `}
          >
            View all evaluations and their current status. See what's running right now and what is not running.
          </Text>
        </div>
        <div css={lastUpdatedContainer} onClick={() => fetchStatuses()}>
          <Icon svg={<Icons.Refresh />} />
          <Text>Last updated: {formatDateTime(lastUpdated)}</Text>
        </div>
      </div>

      {loading ? (
        <div css={css`min-height: 300px; display: grid; place-items: center;`}>
          <Loading />
        </div>
      ) : (
        <>
          {/* Summary Cards Grid */}
          <div css={cardsGrid}>
            <div css={summaryCard("green")}>
              <div className="icon-badge">
                <Icon svg={<Icons.PlayCircleOutline />} />
              </div>
              <div className="card-content">
                <div className="card-value">{runningEvaluations.length}</div>
                <div className="card-title">Running</div>
              </div>
            </div>

            <div css={summaryCard("grey")}>
              <div className="icon-badge">
                <Icon svg={<Icons.StopCircleOutline />} />
              </div>
              <div className="card-content">
                <div className="card-value">{notRunningEvaluations.length}</div>
                <div className="card-title">Evaluation History</div>
              </div>
            </div>

            <div css={summaryCard("blue")}>
              <div className="icon-badge">
                <Icon svg={<Icons.ClockOutline />} />
              </div>
              <div className="card-content">
                <div className="card-value" css={css`font-size: 0.95rem; line-height: 1.3; font-weight: 600; padding: 2px 0;`}>
                  {latestStartedAt ? formatDateTime(latestStartedAt) : "Never"}
                </div>
                <div className="card-title">Last Started</div>
              </div>
            </div>

            <div css={summaryCard("orange")}>
              <div className="icon-badge">
                <Icon svg={<Icons.CalendarOutline />} />
              </div>
              <div className="card-content">
                <div className="card-value">{groupedStatuses.length}</div>
                <div className="card-title">Total Evaluations</div>
              </div>
            </div>
          </div>

          {/* Tables layout grid */}
          <div css={tablesLayoutGrid}>
            {/* Running Evaluations Column */}
            <div css={tableSectionCard("green")}>
              <div css={tableHeader("green")}>
                <div className="title-row">
                  <div className="status-dot" />
                  <Heading level={3}>Running Evaluations ({runningEvaluations.length})</Heading>
                </div>
                <div className="subtitle">Evaluations that are currently running</div>
              </div>

              <div css={tableContainer}>
                <table css={customTable}>
                  <thead>
                    <tr>
                      <th>Evaluation</th>
                      <th>Category</th>
                      <th>Project</th>
                      <th>Started At</th>
                      <th style={{ width: "40px" }} />
                    </tr>
                  </thead>
                  <tbody>
                    {runningEvaluations.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center", color: "var(--global-text-color-500)", padding: "2rem" }}>
                          No evaluations are currently running.
                        </td>
                      </tr>
                    ) : (
                      displayedRunning.map((evalItem) => {
                        const isDataset = evalItem.category === "dataset_evaluation";
                        return (
                          <tr key={evalItem.template_id}>
                            <td>
                              <div className="eval-cell">
                                <span className="status-indicator-icon running">
                                  <Icon svg={<Icons.PlayCircleOutline />} />
                                </span>
                                <span>{evalItem.name}</span>
                              </div>
                            </td>
                            <td>{formatCategory(evalItem.category)}</td>
                            <td>{evalItem.project_name || "All Projects"}</td>
                            <td>{formatDateTime(evalItem.started_at)}</td>
                            <td>
                              <MenuTrigger>
                                <Button
                                  size="S"
                                  leadingVisual={<Icon svg={<Icons.MoreHorizontalOutline />} />}
                                />
                                <Popover>
                                  <Menu
                                    aria-label="Running evaluation menu"
                                    onAction={(action) => {
                                      if (action === "STOP") {
                                        handleStopMonitor(
                                          evalItem.template_id,
                                          evalItem.name,
                                          isDataset,
                                          null
                                        );
                                      }
                                    }}
                                  >
                                    <MenuItem id="STOP" style={{ color: "#EF4444" }}>
                                      <Flex direction="row" gap="size-75" justifyContent="start" alignItems="center">
                                        <Icon svg={<Icons.StopCircleOutline />} />
                                        <span>Stop Evaluation</span>
                                      </Flex>
                                    </MenuItem>
                                  </Menu>
                                </Popover>
                              </MenuTrigger>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {runningEvaluations.length > 8 && (
                <div css={footerLinkRow}>
                  <div css={footerLink} onClick={() => setShowAllRunning(!showAllRunning)}>
                    <span>{showAllRunning ? "Show less evaluations" : "View all running evaluations"}</span>
                    <Icon svg={<Icons.ArrowRight />} />
                  </div>
                </div>
              )}
            </div>

            {/* Not Running Evaluations Column */}
            <div css={tableSectionCard("grey")}>
              <div css={tableHeader("grey")}>
                <div className="title-row">
                  <div className="status-dot" />
                  <Heading level={3}>Evaluation History ({notRunningEvaluations.length})</Heading>
                </div>
                <div className="subtitle">History of evaluations and last run details</div>
              </div>

              <div css={tableContainer}>
                <table css={customTable}>
                  <thead>
                    <tr>
                      <th>Evaluation</th>
                      <th>Category</th>
                      <th>Project</th>
                      <th>Last Stopped / Last Run</th>
                      <th style={{ width: "40px" }} />
                    </tr>
                  </thead>
                  <tbody>
                    {notRunningEvaluations.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center", color: "var(--global-text-color-500)", padding: "2rem" }}>
                          No evaluations are currently stopped.
                        </td>
                      </tr>
                    ) : (
                      displayedNotRunning.map((evalItem) => {
                        const isNeverStarted = !evalItem.stopped_at && !evalItem.started_at;
                        return (
                          <tr key={evalItem.template_id}>
                            <td>
                              <div className="eval-cell">
                                <span className="status-indicator-icon stopped">
                                  <Icon svg={<Icons.StopCircleOutline />} />
                                </span>
                                <span>{evalItem.name}</span>
                              </div>
                            </td>
                            <td>{formatCategory(evalItem.category)}</td>
                            <td>{evalItem.project_name || "—"}</td>
                            <td>
                              {isNeverStarted ? (
                                <span className="never-started">Never Started</span>
                              ) : (
                                formatDateTime(evalItem.stopped_at || evalItem.started_at)
                              )}
                            </td>
                            <td>
                              <MenuTrigger>
                                <Button
                                  size="S"
                                  leadingVisual={<Icon svg={<Icons.MoreHorizontalOutline />} />}
                                />
                                <Popover>
                                  <Menu
                                    aria-label="Stopped evaluation menu"
                                    onAction={(action) => {
                                      if (action === "START") {
                                        handleStartMonitor(evalItem.category, evalItem.template_id);
                                      }
                                    }}
                                  >
                                    <MenuItem id="START">
                                      <Flex direction="row" gap="size-75" justifyContent="start" alignItems="center">
                                        <Icon svg={<Icons.PlayCircleOutline />} />
                                        <span>Start / Configure</span>
                                      </Flex>
                                    </MenuItem>
                                  </Menu>
                                </Popover>
                              </MenuTrigger>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {notRunningEvaluations.length > 8 && (
                <div css={footerLinkRow}>
                  <div css={footerLink} onClick={() => setShowAllNotRunning(!showAllNotRunning)}>
                    <span>{showAllNotRunning ? "Show less evaluations" : "View all evaluation history"}</span>
                    <Icon svg={<Icons.ArrowRight />} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Info Alert Banner */}
          <div css={bottomAlertBanner}>
            <span className="info-icon">
              <Icon svg={<Icons.AlertCircleFilled />} />
            </span>
            <Text>Evaluations update automatically. Pull to refresh or click the refresh icon to get the latest status.</Text>
          </div>
        </>
      )}
    </div>
  );
}
